import React, { useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import CustomerApiService from "../../CustomerApiService";
import DelayedSpinner from "../../components/DelayedSpinner";
import { useInterfaceAccess } from "../../hooks/useInterfaceAccess";
import { SPOTTER_INTERFACE_ID } from "../spotter/config";
import RemoteApp from "./RemoteApp";
import type { LoaderDeps, RemoteMountOptions } from "./loadRemoteApp";

export const SPOTTER_MANIFEST_PATH = "/remote/spotter/manifest.json";

/** Build-time switch: `REACT_APP_SPOTTER_REMOTE_UI=1` serves `/spotter/*` from the remote
 *  bundle; anything else keeps the in-tree Spotter routes. */
export const spotterRemoteEnabled = (): boolean =>
  process.env.REACT_APP_SPOTTER_REMOTE_UI === "1";

export interface SpotterRemoteRouteProps {
  /** The in-tree Spotter, rendered if the remote bundle cannot be loaded or mounted. */
  fallback: React.ReactNode;
  deps?: LoaderDeps;
}

/**
 * Mounts the remote Spotter UI for every `/spotter/*` path once the org is entitled to it. The
 * access check runs first so the boot `/session/exchange` has settled before Spotter's own
 * requests go out. Spotter runs its own router under `/spotter`, and its 401s fire the same
 * `customer-session-expired` event `useInterfaceAccess` already listens for.
 */
const SpotterRemoteRoute = ({ fallback, deps }: SpotterRemoteRouteProps) => {
  const access = useInterfaceAccess(SPOTTER_INTERFACE_ID);
  // The remote keeps the function it was mounted with, so route through the latest navigate.
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const options = useMemo<RemoteMountOptions>(
    () => ({
      basename: "/spotter",
      apiBaseUrl: CustomerApiService.apiUrl,
      env: process.env.REACT_APP_ENV ?? "",
      navigate: (to: string) => navigateRef.current(to),
    }),
    []
  );

  if (access.status === "pending") {
    return <DelayedSpinner padTop />;
  }
  // The in-tree screens already render a denial inside the Spotter chrome.
  if (access.status === "denied") {
    return <>{fallback}</>;
  }
  return (
    <RemoteApp
      name="Spotter"
      manifestPath={SPOTTER_MANIFEST_PATH}
      options={options}
      fallback={() => fallback}
      deps={deps}
    />
  );
};

export default SpotterRemoteRoute;
